import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { leadSources, leadStatuses, company } from "../mock";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import {
  Users, Inbox, TrendingUp, Building2, ArrowLeft, Search, Upload,
  Hospital, HeartHandshake, Globe, Home, LogOut, PhoneCall, Phone, Mail, Star,
} from "lucide-react";
import OwnerLogin from "../components/OwnerLogin";
import EmailModal from "../components/EmailModal";
import BulkEmailModal from "../components/BulkEmailModal";
import MarketingPanel from "../components/MarketingPanel";
import PayersPanel from "../components/PayersPanel";
import GrowthPanel from "../components/GrowthPanel";
import AcquisitionPanel from "../components/AcquisitionPanel";
import OutreachPanel from "../components/OutreachPanel";
import ReviewsPanel from "../components/ReviewsPanel";
import { leadsApi } from "../api";
import { useToast } from "../hooks/use-toast";

const statusColors = {
  New: "bg-amber-100 text-amber-700 border-amber-200",
  Contacted: "bg-blue-100 text-blue-700 border-blue-200",
  "Consultation Scheduled": "bg-violet-100 text-violet-700 border-violet-200",
  "Intake Started": "bg-indigo-100 text-indigo-700 border-indigo-200",
  "Authorization Review": "bg-cyan-100 text-cyan-700 border-cyan-200",
  "Staffing Review": "bg-orange-100 text-orange-700 border-orange-200",
  "Ready to Start": "bg-emerald-100 text-emerald-700 border-emerald-200",
  Won: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Lost: "bg-red-100 text-red-700 border-red-200",
  Nurture: "bg-slate-100 text-slate-600 border-slate-200",
  Assessment: "bg-violet-100 text-violet-700 border-violet-200",
  Client: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

const tempColors = {
  HOT: "bg-red-100 text-red-700 border-red-200",
  WARM: "bg-amber-100 text-amber-700 border-amber-200",
  NURTURE: "bg-slate-100 text-slate-600 border-slate-200",
  "LOW FIT": "bg-slate-100 text-slate-400 border-slate-200",
};

const sourceIcons = {
  Hospital: Hospital,
  "Nursing Home": Building2,
  Family: HeartHandshake,
  Website: Globe,
  Uploaded: Upload,
};

const Dashboard = () => {
  const { toast } = useToast();
  const [authed, setAuthed] = useState(!!localStorage.getItem("cac_token"));
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [query, setQuery] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [emailLead, setEmailLead] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [tab, setTab] = useState("leads");
  const fileRef = useRef(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (sourceFilter !== "all") params.source = sourceFilter;
      if (query) params.q = query;
      const [l, s] = await Promise.all([leadsApi.list(params), leadsApi.stats()]);
      setLeads(l.data);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("cac_token");
        setAuthed(false);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, sourceFilter]);

  // debounce search
  useEffect(() => {
    if (!authed) return;
    const t = setTimeout(loadData, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const logout = () => {
    localStorage.removeItem("cac_token");
    setAuthed(false);
  };

  const askReview = async (l) => {
    try {
      const r = await leadsApi.requestReview(l.id);
      setLeads((prev) => prev.map((x) => (x.id === l.id ? { ...x, reviewRequested: true } : x)));
      toast({ title: "Review request sent!", description: `A friendly email was sent to ${r.data.to}.` });
    } catch (err) {
      toast({ title: "Could not send", description: err?.response?.data?.detail || "Please try again.", variant: "destructive" });
    }
  };

  const updateLead = async (id, patch) => {
    // optimistic
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
    try {
      await leadsApi.update(id, patch);
      leadsApi.stats().then((s) => setStats(s.data));
    } catch {
      toast({ title: "Update failed", variant: "destructive" });
      loadData();
    }
  };

  const handleCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((x) => x.trim());
    if (!lines.length) return;
    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
    const idx = (k) => headers.indexOf(k);
    const rows = lines.slice(1).map((line) => {
      const cols = line.split(",");
      const get = (k) => (idx(k) >= 0 ? (cols[idx(k)] || "").trim() : "");
      const name = get("name") || cols[0]?.trim() || "";
      return {
        name,
        email: get("email"),
        phone: get("phone"),
        contact: get("phone") || get("email") || get("contact"),
        org: get("org") || get("organization") || get("facility") || "—",
        note: get("note") || get("notes") || "",
      };
    }).filter((r) => r.name);
    try {
      const res = await leadsApi.bulk(rows);
      toast({ title: "Import complete", description: `${res.data.inserted} contacts added to your call list.` });
      loadData();
    } catch {
      toast({ title: "Import failed", description: "Check the CSV format and try again.", variant: "destructive" });
    }
    e.target.value = "";
  };

  const maxSource = Math.max(1, ...(stats?.bySource || []).map((b) => b.count));

  const statCards = stats ? [
    { label: "Total Leads", value: stats.total, icon: Inbox, accent: "#0E5A72" },
    { label: "New Leads", value: stats.newLeads, icon: TrendingUp, accent: "#D97706" },
    { label: "Facility Referrals", value: stats.facility, icon: Hospital, accent: "#7C3AED" },
    { label: "Active Clients", value: stats.clients, icon: Users, accent: "#2E9E6A" },
  ] : [];

  const callList = useMemo(
    () => leads.filter((l) => !l.called && l.status !== "Client"),
    [leads]
  );

  if (!authed) return <OwnerLogin onSuccess={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-[#F4F8FA]">
      <div className="brand-gradient text-white">
        <div className="mx-auto max-w-7xl px-4 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/15 h-11 w-11 rounded-xl grid place-items-center font-display font-extrabold">CAC</div>
            <div>
              <div className="font-display font-bold text-lg">Owner Dashboard</div>
              <div className="text-white/80 text-sm">Lead management • {company.shortName}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/">
              <Button className="rounded-full bg-white/15 hover:bg-white/25 text-white border-0">
                <ArrowLeft className="mr-2 h-4 w-4" /> Site
              </Button>
            </Link>
            <Button onClick={logout} className="rounded-full bg-white/15 hover:bg-white/25 text-white border-0">
              <LogOut className="mr-2 h-4 w-4" /> Log out
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8">
        <div className="inline-flex rounded-full bg-white border border-slate-200 p-1 shadow-sm mb-6">
          <button
            onClick={() => setTab("leads")}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${tab === "leads" ? "bg-[#0E5A72] text-white" : "text-slate-600 hover:text-[#0E5A72]"}`}
          >
            Leads
          </button>
          <button
            onClick={() => setTab("marketing")}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${tab === "marketing" ? "bg-[#2E9E6A] text-white" : "text-slate-600 hover:text-[#23875A]"}`}
          >
            Marketing
          </button>
          <button
            data-testid="acquisition-tab-btn"
            onClick={() => setTab("acquisition")}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${tab === "acquisition" ? "bg-[#DC2626] text-white" : "text-slate-600 hover:text-[#DC2626]"}`}
          >
            Client Acquisition
          </button>
          <button
            data-testid="outreach-tab-btn"
            onClick={() => setTab("outreach")}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${tab === "outreach" ? "bg-[#2E9E6A] text-white" : "text-slate-600 hover:text-[#2E9E6A]"}`}
          >
            Outreach
          </button>
          <button
            data-testid="payers-tab-btn"
            onClick={() => setTab("payers")}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${tab === "payers" ? "bg-[#0E5A72] text-white" : "text-slate-600 hover:text-[#0E5A72]"}`}
          >
            Payers & Programs
          </button>
          <button
            data-testid="reviews-tab-btn"
            onClick={() => setTab("reviews")}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${tab === "reviews" ? "bg-[#D97706] text-white" : "text-slate-600 hover:text-[#D97706]"}`}
          >
            Reviews
          </button>
          <button
            data-testid="growth-tab-btn"
            onClick={() => setTab("growth")}
            className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${tab === "growth" ? "bg-[#7C3AED] text-white" : "text-slate-600 hover:text-[#7C3AED]"}`}
          >
            SEO & Growth
          </button>
        </div>

        {tab === "marketing" && <MarketingPanel leads={leads} stats={stats} />}
        {tab === "acquisition" && <AcquisitionPanel />}
        {tab === "outreach" && <OutreachPanel />}
        {tab === "payers" && <PayersPanel />}
        {tab === "reviews" && <ReviewsPanel />}
        {tab === "growth" && <GrowthPanel />}

        {tab === "leads" && (<>
        {/* Stats */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {statCards.map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.label} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 card-hover">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-slate-500">{c.label}</div>
                  <div className="h-10 w-10 rounded-xl grid place-items-center" style={{ backgroundColor: `${c.accent}1a`, color: c.accent }}>
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
                <div className="font-display font-extrabold text-3xl mt-3" style={{ color: c.accent }}>{c.value}</div>
              </div>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          {/* Sidebar: sources + call list */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <h3 className="font-display font-bold text-[#0E2A33]">Leads by Source</h3>
              <p className="text-sm text-slate-500 mb-4">Where your leads come from</p>
              <div className="space-y-4">
                {(stats?.bySource || []).map((b) => {
                  const Icon = sourceIcons[b.source] || Globe;
                  return (
                    <div key={b.source}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="flex items-center gap-2 text-slate-700 font-medium">
                          <Icon className="h-4 w-4 text-[#0E5A72]" /> {b.source}
                        </span>
                        <span className="text-slate-500">{b.count}</span>
                      </div>
                      <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full bg-[#2E9E6A] transition-all" style={{ width: `${(b.count / maxSource) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-6 pt-4 border-t border-slate-100 text-center">
                <div className="font-display font-extrabold text-2xl text-[#0E5A72]">{stats?.conversion ?? 0}%</div>
                <div className="text-sm text-slate-500">Lead → Client conversion</div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-display font-bold text-[#0E2A33] flex items-center gap-2">
                  <PhoneCall className="h-5 w-5 text-[#2E9E6A]" /> Call List
                </h3>
                <Badge className="bg-[#E9F6EF] text-[#23875A] border border-emerald-200">{callList.length}</Badge>
              </div>
              <p className="text-sm text-slate-500 mb-3">People to call today</p>
              <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
                {callList.length === 0 && <div className="text-sm text-slate-400 py-4 text-center">All caught up! 🎉</div>}
                {callList.map((l) => (
                  <div key={l.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2">
                    <div className="min-w-0">
                      <div className="font-medium text-[#0E2A33] text-sm truncate">{l.name}</div>
                      <div className="text-xs text-slate-500 truncate">{l.phone || l.contact}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <a href={`tel:${(l.phone || l.contact || "").replace(/[^0-9]/g, "")}`}>
                        <Button size="icon" variant="outline" className="h-8 w-8"><Phone className="h-4 w-4 text-[#0E5A72]" /></Button>
                      </a>
                      <Button size="sm" className="h-8 bg-[#2E9E6A] hover:bg-[#23875A] text-white text-xs px-2"
                        onClick={() => updateLead(l.id, { called: true, status: l.status === "New" ? "Contacted" : l.status })}>
                        Done
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Lead inbox */}
          <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <h3 className="font-display font-bold text-[#0E2A33]">Lead Inbox</h3>
              <div className="flex flex-wrap gap-2">
                <div className="relative">
                  <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name / facility" className="pl-9 h-10 w-44" />
                </div>
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger className="h-10 w-36"><SelectValue placeholder="Source" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All sources</SelectItem>
                    {[...leadSources, "Uploaded", "Family Caregiver SEO"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleCsv} />
                <Button variant="outline" className="h-10 border-[#2E9E6A] text-[#23875A]" onClick={() => setBulkOpen(true)}>
                  <Mail className="mr-2 h-4 w-4" /> Email List
                </Button>
                <Button variant="outline" className="h-10 border-[#0E5A72] text-[#0E5A72]" onClick={() => fileRef.current?.click()}>
                  <Upload className="mr-2 h-4 w-4" /> Import CSV
                </Button>
              </div>
            </div>

            <div className="space-y-3 max-h-[620px] overflow-y-auto pr-1">
              {loading && <div className="text-center text-slate-400 py-8">Loading…</div>}
              {!loading && leads.length === 0 && (
                <div className="text-center text-slate-400 py-12">No leads match your filters.</div>
              )}
              {leads.map((l) => {
                const Icon = sourceIcons[l.source] || Globe;
                return (
                  <div key={l.id} className="rounded-xl border border-slate-100 p-4 hover:border-[#2E9E6A]/40 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-[#0E2A33]">{l.name}</span>
                          <Badge variant="outline" className="text-xs gap-1 border-slate-200 text-slate-600">
                            <Icon className="h-3 w-3" /> {l.source}
                          </Badge>
                          {l.called && <Badge className="text-xs bg-emerald-100 text-emerald-700 border border-emerald-200">Called</Badge>}
                          {l.temperature && <Badge className={`text-xs border ${tempColors[l.temperature] || ""}`}>{l.temperature}{l.leadScore ? ` · ${l.leadScore}` : ""}</Badge>}
                          {l.possibleDuplicate && <Badge className="text-xs bg-orange-100 text-orange-700 border border-orange-200">POSSIBLE DUPLICATE</Badge>}
                        </div>
                        <div className="text-sm text-slate-500 mt-0.5">
                          {l.contact}{l.org && l.org !== "—" ? ` • ${l.org}` : ""}
                        </div>
                        {l.note && <p className="text-sm text-slate-600 mt-2">{l.note}</p>}
                        <div className="text-xs text-slate-400 mt-2">Received {l.createdAt}</div>
                      </div>
                      <div className="shrink-0 text-right space-y-2">
                        <Badge className={`border ${statusColors[l.status]}`}>{l.status}</Badge>
                        <Select value={l.status} onValueChange={(v) => updateLead(l.id, { status: v })}>
                          <SelectTrigger className="h-9 w-36"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {leadStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="outline" className="w-36" onClick={() => setEmailLead(l)}>
                          <Mail className="mr-2 h-4 w-4 text-[#0E5A72]" /> Email
                        </Button>
                        {(l.email || (l.contact || "").includes("@")) && !l.reviewRequested && (
                          <Button data-testid="ask-review-btn" size="sm" variant="outline" className="w-36 border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => askReview(l)}>
                            <Star className="mr-2 h-4 w-4" /> Ask for Review
                          </Button>
                        )}
                        {l.reviewRequested && (
                          <Badge className="text-xs bg-amber-100 text-amber-700 border border-amber-200 w-36 justify-center">Review requested</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Pipeline */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mt-6">
          <h3 className="font-display font-bold text-[#0E2A33] mb-4 flex items-center gap-2">
            <Home className="h-5 w-5 text-[#0E5A72]" /> Status Pipeline
          </h3>
          <div className="grid gap-4 md:grid-cols-5">
            {leadStatuses.map((s) => {
              const items = leads.filter((l) => l.status === s);
              return (
                <div key={s} className="bg-[#F7FBFC] rounded-xl p-4 border border-slate-100">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-semibold text-[#0E2A33]">{s}</span>
                    <Badge className={`border ${statusColors[s]}`}>{items.length}</Badge>
                  </div>
                  <div className="space-y-2">
                    {items.slice(0, 6).map((l) => (
                      <div key={l.id} className="bg-white rounded-lg px-3 py-2 text-sm text-slate-700 shadow-sm border border-slate-100 truncate">
                        {l.name}
                      </div>
                    ))}
                    {items.length === 0 && <div className="text-xs text-slate-400">Empty</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        </>)}
      </div>

      <EmailModal open={!!emailLead} onOpenChange={(o) => !o && setEmailLead(null)} lead={emailLead} />
      <BulkEmailModal open={bulkOpen} onOpenChange={setBulkOpen} leads={leads} onSent={loadData} />
    </div>
  );
};

export default Dashboard;
